/**
 * leadsApi — Service-Layer fuer alle Backend-Endpoints.
 *
 * Kapselt JEDEN HTTP-Call. Komponenten und Stores duerfen NIE direkt eine
 * URL bilden — sie nutzen ausschliesslich diese Wrapper.
 *
 * Backend-Base: /programmes/leadmanagement-teamshops
 * Response-Format: { success: boolean, data?: T, error?: { code, message } }
 *
 * Nutzt @libero/core/services/api fuer 401-Auto-Refresh und CORS-Cookies.
 */

import { api } from '@libero/core/services/api';
import type { Lead, LeadEvent, LeadMail, LeadTask, LeadTemplate, UserMini, DashboardMetrics, ListenerStatus } from '../types';

// ── Bulk-Action-Typen (Phase 4 Plan 04 — ADMIN-03..06) ────────────────────
export type BulkActionType = 'add_tag' | 'remove_tag' | 'change_status' | 'assign_user';

export interface BulkActionRequest {
  leadIds: number[];
  action: BulkActionType;
  payload: {
    tag?: string;
    status?: string;
    userId?: number | null;
  };
}

export interface BulkActionResponse {
  successful: number;
  failed: Array<{ leadId: number; error: string }>;
}

const BASE = '/programmes/leadmanagement-teamshops';

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}

export interface ListLeadsParams {
  status?: string;
  tag?: string;
  assignedUserId?: number;
  search?: string;
}

function unwrap<T>(envelope: ApiEnvelope<T>, op: string): T {
  if (!envelope.success || envelope.data === undefined) {
    throw new Error(envelope.error?.message ?? `${op} fehlgeschlagen`);
  }
  return envelope.data;
}

export const leadsApi = {
  async list(params: ListLeadsParams = {}): Promise<Lead[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.tag) qs.set('tag', params.tag);
    if (params.assignedUserId != null) qs.set('assignedUserId', String(params.assignedUserId));
    if (params.search) qs.set('search', params.search);
    const url = `${BASE}/leads${qs.toString() ? '?' + qs.toString() : ''}`;
    const res = await api.get<ApiEnvelope<Lead[]>>(url);
    return res.data.data ?? [];
  },

  async get(id: number): Promise<Lead> {
    const res = await api.get<ApiEnvelope<Lead>>(`${BASE}/leads/${id}`);
    return unwrap(res.data, 'getLead');
  },

  async create(body: Partial<Lead>): Promise<Lead> {
    const res = await api.post<ApiEnvelope<Lead>>(`${BASE}/leads`, body);
    return unwrap(res.data, 'createLead');
  },

  async update(id: number, body: Partial<Lead>): Promise<Lead> {
    const res = await api.patch<ApiEnvelope<Lead>>(`${BASE}/leads/${id}`, body);
    return unwrap(res.data, 'updateLead');
  },

  async remove(id: number): Promise<void> {
    await api.delete(`${BASE}/leads/${id}`);
  },

  async events(id: number): Promise<LeadEvent[]> {
    const res = await api.get<ApiEnvelope<LeadEvent[]>>(`${BASE}/leads/${id}/events`);
    return res.data.data ?? [];
  },

  // ── Plan 5.1-04: Mailverlauf ─────────────────────────────────────────
  async mails(id: number): Promise<LeadMail[]> {
    const res = await api.get<ApiEnvelope<LeadMail[]>>(`${BASE}/leads/${id}/mails`);
    return res.data.data ?? [];
  },

  async reply(
    id: number,
    body: { to: string; subject: string; body: string },
  ): Promise<{ simulated?: boolean; messageId?: string; message?: string }> {
    const res = await api.post<ApiEnvelope<{ simulated?: boolean; messageId?: string; message?: string }>>(
      `${BASE}/leads/${id}/reply`,
      body,
    );
    return res.data.data ?? {};
  },

  async addTag(id: number, tag: string): Promise<Lead> {
    const res = await api.post<ApiEnvelope<Lead>>(`${BASE}/leads/${id}/tags`, { tag });
    return unwrap(res.data, 'addTag');
  },

  async removeTag(id: number, tag: string): Promise<Lead> {
    const res = await api.delete<ApiEnvelope<Lead>>(
      `${BASE}/leads/${id}/tags/${encodeURIComponent(tag)}`,
    );
    return unwrap(res.data, 'removeTag');
  },

  async logCall(
    id: number,
    body: { calledAt?: string; durationMinutes: number; resultNote: string },
  ): Promise<LeadEvent> {
    const res = await api.post<ApiEnvelope<LeadEvent>>(`${BASE}/leads/${id}/call-log`, body);
    return unwrap(res.data, 'logCall');
  },

  /**
   * User-Liste fuer "Zugewiesen an"-Dropdown.
   * Programm-eigener Endpoint (statt Core /api/users) — Non-Admin-User
   * koennen sonst keine Liste laden (Core verlangt manage:users).
   */
  async listUsers(): Promise<UserMini[]> {
    const res = await api.get<ApiEnvelope<UserMini[]>>(`${BASE}/users`);
    return res.data.data ?? [];
  },

  // ── Templates (Phase 4 Plan 02 — Mail-Template-Editor) ──────────────
  templates: {
    /** Alle 3 Mail-Vorlagen laden (erstkontakt, followup_1, followup_2). */
    async list(): Promise<LeadTemplate[]> {
      const res = await api.get<ApiEnvelope<LeadTemplate[]>>(`${BASE}/templates`);
      return res.data.data ?? [];
    },

    /** Einzelne Vorlage laden. */
    async get(key: string): Promise<LeadTemplate> {
      const res = await api.get<ApiEnvelope<LeadTemplate>>(`${BASE}/templates/${key}`);
      return unwrap(res.data, 'getTemplate');
    },

    /** Vorlage speichern (PUT — vollstaendiger Ersatz). */
    async update(
      key: string,
      body: { subject: string; bodyText: string; bodyHtml?: string | null },
    ): Promise<LeadTemplate> {
      const res = await api.put<ApiEnvelope<LeadTemplate>>(
        `${BASE}/templates/${key}`,
        body,
      );
      return unwrap(res.data, 'updateTemplate');
    },
  },

  // ── Dashboard (Phase 4 Plan 03 — KPI-Metriken) ──────────────────────
  dashboard: {
    /** Alle 5 Dashboard-KPIs in einer Request laden. Auto-Refresh alle 60s in DashboardPage. */
    async getMetrics(): Promise<DashboardMetrics> {
      const res = await api.get<ApiEnvelope<DashboardMetrics>>(`${BASE}/dashboard/metrics`);
      return unwrap(res.data, 'getDashboardMetrics');
    },
  },

  // ── Bulk-Actions (Phase 4 Plan 04 — ADMIN-03..06) ────────────────────
  bulk: {
    /** POST /leads/bulk-action — Fuehrt Bulk-Aktion fuer mehrere Leads aus. */
    async execute(req: BulkActionRequest): Promise<BulkActionResponse> {
      const res = await api.post<ApiEnvelope<BulkActionResponse>>(
        `${BASE}/leads/bulk-action`,
        req,
      );
      return unwrap(res.data, 'bulkAction');
    },
  },

  // ── Tasks (Phase 4 Plan 01 — Tasks/Follow-ups) ────────────────────────
  tasks: {
    /** Liste aller Tasks fuer einen Lead (offene + abgehakte). */
    async listForLead(leadId: number): Promise<LeadTask[]> {
      const res = await api.get<ApiEnvelope<LeadTask[]>>(
        `${BASE}/tasks?leadId=${leadId}`,
      );
      return res.data.data ?? [];
    },

    /** Aggregations-Liste fuer Board-Header-Panel: alle offenen Tasks mit dueDate <= upperIso. */
    async listByDue(upperIso: string): Promise<LeadTask[]> {
      const qs = new URLSearchParams({
        dueBefore: upperIso,
        completed: 'false',
      });
      const res = await api.get<ApiEnvelope<LeadTask[]>>(
        `${BASE}/tasks?${qs.toString()}`,
      );
      return res.data.data ?? [];
    },

    /** Neuen Task anlegen — Backend setzt task_created Event automatisch. */
    async create(body: {
      leadId: number;
      title: string;
      dueDate: string;
    }): Promise<LeadTask> {
      const res = await api.post<ApiEnvelope<LeadTask>>(`${BASE}/tasks`, body);
      return unwrap(res.data, 'createTask');
    },

    /** Task abhaken (oder zuruecksetzen). Backend setzt task_completed Event automatisch. */
    async setCompleted(taskId: number, completed: boolean): Promise<LeadTask> {
      const res = await api.patch<ApiEnvelope<LeadTask>>(
        `${BASE}/tasks/${taskId}`,
        { completed },
      );
      return unwrap(res.data, 'setCompleted');
    },

    /** Task loeschen. */
    async remove(taskId: number): Promise<void> {
      await api.delete(`${BASE}/tasks/${taskId}`);
    },
  },

  // ── Listener-Status (Phase 5 Plan 02 — ADMIN-07 Read-Only) ──────────
  listenerStatus: {
    /** GET /listener-status — liest aktuellen Listener-Job aus JobHistory. */
    async get(): Promise<ListenerStatus> {
      const res = await api.get<ApiEnvelope<ListenerStatus>>(`${BASE}/listener-status`);
      return unwrap(res.data, 'listenerStatus.get');
    },
  },
};
